import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { io } from 'socket.io-client';
import {
  POINT_RULES,
  achievementById,
  type ChannelSummary,
  type Membership,
  type PresenceStatus,
} from '@itsappy/shared';
import { useQueryClient } from '@tanstack/react-query';
import { API_URL } from '@/lib/config';
import { tokenStore } from '@/lib/api';
import { useAuth } from '@/store/auth';
import { toast, useUi } from '@/store/ui';
import { useCall } from '@/store/call';
import { useWalkie } from '@/store/walkie';
import { endWalkiePlayback, playWalkieChunk } from '@/media/walkie';
import { stampAcks, stampMedia } from '@/lib/media';
import { SocketContext, type AppSocket } from './socketContext';

export function SocketProvider({ children }: { children: ReactNode }) {
  const user = useAuth((s) => s.user);
  const userId = user?.id;
  const [connected, setConnected] = useState(false);
  // State rather than a ref: consumers need to re-render when the socket is
  // created, and a ref mutation does not trigger that.
  const [socket, setSocket] = useState<AppSocket | null>(null);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  useEffect(() => {
    if (!userId) {
      setSocket(null);
      setConnected(false);
      return;
    }

    const instance: AppSocket = io(API_URL, {
      auth: { token: tokenStore.access },
      transports: ['websocket'],
      reconnectionDelay: 500,
      reconnectionDelayMax: 5000,
    });

    /* Broadcasts skip the REST client entirely, so they need the same stamp —
       a message that arrives live carries exactly the same attachment URLs as
       the same message fetched from history. socket.io runs these before the
       per-event listeners, and the rewrite is in place, so by the time a
       handler reads the payload the URLs already carry the pass. */
    instance.onAny((_event, ...args: unknown[]) => stampMedia(args));
    // An ack is not a broadcast, so `onAny` never sees it. Same stamp, other door.
    stampAcks(instance);

    const updateMemberPresence = (
      userId: string,
      status: PresenceStatus,
      statusMessage?: string,
      office?: { atOffice?: boolean; officeSince?: string; officeSiteName?: string },
      doNotDisturb?: boolean,
    ) => {
      queryClient.setQueryData<{ items: Membership[] }>(['members'], (current) =>
        current
          ? {
              ...current,
              items: current.items.map((member) =>
                member.user.id === userId
                  ? {
                      ...member,
                      user: {
                        ...member.user,
                        status,
                        statusMessage,
                        // A plain status change says nothing about the office,
                        // so the cached flag survives unless the event carries one.
                        ...(office?.atOffice === undefined
                          ? {}
                          : {
                              atOffice: office.atOffice || undefined,
                              officeSince: office.atOffice ? office.officeSince : undefined,
                              officeSiteName: office.atOffice
                                ? office.officeSiteName
                                : undefined,
                            }),
                        // Same bargain: only a pause or quiet-hours change
                        // carries the flag, so keep the cached one otherwise.
                        ...(doNotDisturb === undefined ? {} : { doNotDisturb: doNotDisturb || undefined }),
                        lastSeenAt:
                          status === 'offline'
                            ? new Date().toISOString()
                            : member.user.lastSeenAt,
                      },
                    }
                  : member,
              ),
            }
          : current,
      );
    };

    instance.on('connect', () => {
      setConnected(true);
      const currentUser = useAuth.getState().user;
      updateMemberPresence(userId, 'online', currentUser?.statusMessage);
      if (currentUser && currentUser.status !== 'online') {
        useAuth.getState().setUser({ ...currentUser, status: 'online' });
      }
      void queryClient.invalidateQueries({ queryKey: ['members'] });
    });
    instance.on('disconnect', () => setConnected(false));
    instance.on('connect_error', (err) => {
      setConnected(false);
      // A stale access token is the common case; the next refresh fixes it.
      if (err.message.includes('token')) instance.auth = { token: tokenStore.access };
    });

    // Notifications and unread badges are global, so they live here rather
    // than in whichever page happens to be mounted.
    instance.on('notification:new', ({ notification }) => {
      queryClient.invalidateQueries({ queryKey: ['notifications'] });
      const chatMatch = /^\/chat\/([^/]+)$/.exec(notification.link ?? '');
      toast.info(notification.title, notification.body, {
        label: chatMatch ? 'Open message' : 'Open notification',
        onClick: () => {
          if (chatMatch) useUi.getState().setChat(true, chatMatch[1]);
          else if (notification.link) navigate(notification.link);
          else useUi.getState().setNotifications(true);
        },
      });
    });

    instance.on('channel:unread', (update) => {
      queryClient.setQueryData<{ items: ChannelSummary[] }>(['channels'], (current) =>
        current
          ? {
              ...current,
              items: current.items.map((channel) =>
                channel.id === update.channelId
                  ? {
                      ...channel,
                      unreadCount: update.unreadCount,
                      mentionCount: update.mentionCount,
                      ...(update.lastMessageAt
                        ? {
                            lastMessageAt: update.lastMessageAt,
                            lastMessagePreview: update.lastMessagePreview,
                          }
                        : {}),
                    }
                  : channel,
              ),
            }
          : current,
      );
    });

    instance.on('channel:created', () => {
      void queryClient.invalidateQueries({ queryKey: ['channels'] });
    });

    // An owner changed workspace administration. Re-reading `/auth/me` is what
    // moves a product off the navigation for everybody who is already looking
    // at it, instead of leaving them on a screen that now returns 403s.
    instance.on('org:updated', () => {
      void useAuth.getState().bootstrap();
      void queryClient.invalidateQueries({ queryKey: ['workspace'] });
    });

    /* You earned points. The event carries everything the toast needs, so no
       refetch stands between the deed and the reward — the caches are only
       invalidated so the engagement page agrees when it is next looked at. */
    instance.on('engagement:points', ({ kind, amount, streak }) => {
      toast.success(
        `+${amount} · ${POINT_RULES[kind].label}`,
        streak ? `${streak.current}-day streak` : undefined,
      );
      void queryClient.invalidateQueries({ queryKey: ['engagement'] });
    });

    /* Kudos are broadcast to the whole workspace; refetching only what is on
       screen keeps the feed live wherever it happens to be open. */
    instance.on('engagement:kudos', () => {
      void queryClient.invalidateQueries({ queryKey: ['engagement', 'kudos'] });
      void queryClient.invalidateQueries({ queryKey: ['engagement', 'leaderboard'] });
    });

    /* A badge is personal and one-time, so the toast is the ceremony. Only
       the profile is re-read — nothing else changed. */
    instance.on('engagement:achievement', ({ id }) => {
      const badge = achievementById(id);
      toast.success(
        `Achievement unlocked: ${badge ? `${badge.emoji} ${badge.label}` : id}`,
        badge?.hint,
      );
      void queryClient.invalidateQueries({ queryKey: ['engagement', 'me'] });
    });

    /* A team challenge changed — created, progressed over the line, or called
       off. The whole workspace hears it; only the win gets a toast. */
    instance.on('engagement:challenge', ({ challenge }) => {
      if (challenge.status === 'completed') {
        toast.success(
          challenge.rewardPoints > 0
            ? `Challenge met — +${challenge.rewardPoints} for everyone 🎉`
            : 'Challenge met 🎉',
          challenge.title,
        );
      }
      void queryClient.invalidateQueries({ queryKey: ['engagement', 'challenges'] });
    });

    instance.on('call:incoming', ({ call }) => {
      useCall.getState().ring(call);
    });

    instance.on('call:updated', ({ call }) => {
      const incoming = useCall.getState().incoming;
      if (incoming?.id === call.id) useCall.getState().ring(call);
    });

    instance.on('call:ended', ({ callId }) => {
      useCall.getState().ended(callId);
    });

    /* The radio is answered here rather than in the channel page: a walkie
       talkie that only played while you already had the channel open would be
       a voice note with extra steps. */
    instance.on('walkie:start', ({ channelId, spaceId, user: speaker }) => {
      useWalkie.getState().began({ user: speaker, target: { channelId, spaceId } });
    });

    instance.on('walkie:chunk', ({ userId: speakerId, mimeType, data }) => {
      // Half duplex, like the hardware: your own held button owns the audio
      // session, and hearing someone else through it would feed back.
      if (useWalkie.getState().sending) return;
      void playWalkieChunk(speakerId, mimeType, data);
    });

    instance.on('walkie:stop', ({ userId: speakerId }) => {
      endWalkiePlayback(speakerId);
      useWalkie.getState().ended(speakerId);
    });

    instance.on(
      'presence:updated',
      ({ userId, status, statusMessage, atOffice, officeSince, officeSiteName, doNotDisturb }) => {
        updateMemberPresence(
          userId,
          status,
          statusMessage,
          { atOffice, officeSince, officeSiteName },
          doNotDisturb,
        );
      },
    );

    instance.on('error', ({ code, message }) => {
      if (code !== 'session_replaced') return;
      toast.info('Signed out', message);
      void useAuth
        .getState()
        .logout()
        .finally(() => navigate('/login'));
    });

    setSocket(instance);

    return () => {
      instance.removeAllListeners();
      instance.disconnect();
      setSocket(null);
    };
  }, [userId, queryClient, navigate]);

  const value = useMemo(() => ({ socket, connected }), [socket, connected]);

  return <SocketContext.Provider value={value}>{children}</SocketContext.Provider>;
}
